import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useEmitter } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { selectJumpDate, selectSelectedDateValue, useReportStore } from '@/entities/report';
import { HR_TYPE } from '@/widgets/hr-summary/ui/helper';
import { IHrSummary, IHrsInDate, IDailySummary } from '@/shared/model';
import type { DailySummaryHrsThumbnailProps } from './dailySummaryHrsThumbnail';

/**
 * Drives the thumbnail strip of min and max heart-rate beats for the selected day, letting the reviewer switch between the min and max tab, toggle ECG channels and correct the min, max or average value. Mirrors hrDataDefault, minHrsInDate and maxHrsInDate into local cloned state, auto-jumps the ECG viewer to the min or max beat time whenever the tab or data changes, prunes a day from the pending edits once it matches the original again, and tracks the render-viewer flag to raise or clear the loading state.
 *
 * @param props - Thumbnail configuration, notably the timezone offset used when converting edited beat times.
 * @returns The active HR tab and its setters, the visible channel list with its toggle, the min and max beat lists, the current HR summary, the loading flag, the thumbnail high-pass and gain settings, the scroll anchor ref, and the min/max/average update handler.
 */
const useDailySummaryHrsThumbnail = ({
  timezoneOffset,
}: Pick<DailySummaryHrsThumbnailProps, 'timezoneOffset'>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const [tabHrActive, setTabHrActive] = useState(HR_TYPE.MIN_HR);
  const [channelsThumbnail, setChannelsThumbnail] = useState([1, 2, 3]);
  const [hrData, setHrData] = useState<IHrSummary>({
    minHr: null,
    maxHr: null,
    avgHr: null,
  } as any);
  const [minHrsInDate, setMinHrsInDate] = useState<IHrsInDate[]>([]);
  const [maxHrsInDate, setMaxHrsInDate] = useState<IHrsInDate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the ECG viewer to a given moment in the HR summary tab. Forwards the value to the shared jump-date action, binding it to the tab-6 store key.
   *
   * @param v - The target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const hrDataDefault = useReportStore((s) => s.hrDataDefault);
  const minHrsInDateRecoil = useReportStore((s) => s.minHrsInDate);
  const maxHrsInDateRecoil = useReportStore((s) => s.maxHrsInDate);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const isRenderViewer = useReportStore((s) => s.isRenderViewerEcg);
  const setSelectedHrTimeAction = useReportStore((s) => s.actions.setSelectedHrTime);
  /**
   * Marks which beat time the HR summary is currently focused on. Forwards the value to the shared selected-HR-time action, binding it to the tab-6 store key.
   *
   * @param v - The beat timestamp, or undefined to clear the selection.
   * @returns Nothing.
   */
  const setSelectedHrTime = (v: string | undefined) => setSelectedHrTimeAction(keyRecoil, v);
  const setViewHrModeAction = useReportStore((s) => s.actions.setViewHrMode);
  /**
   * Switches the HR viewer between minimum and maximum heart-rate mode. Forwards the mode to the shared view-HR-mode action, binding it to the tab-6 store key.
   *
   * @param v - Either the MAX_HR or MIN_HR rhythm-event mode.
   * @returns Nothing.
   */
  const setViewHrMode = (v: TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR) =>
    setViewHrModeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Selects which portion of the jumped-to page the ECG viewer scrolls to. Forwards the value to the shared jump-portion action, binding it to the tab-6 store key.
   *
   * @param v - The portion index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  /**
   * Reads the calendar date currently selected in the HR summary tab. Applies the tab-6 selector to a fresh store snapshot instead of subscribing, so emitter callbacks read the live value.
   *
   * @returns The ISO date string of the selected day.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  /**
   * Reads the locally edited daily heart-rate summaries that are pending save. Pulls updatedDailySummaries off the store snapshot rather than subscribing, keeping emitter handlers free of stale closures.
   *
   * @returns The daily summaries carrying user edits.
   */
  const getUpdatedDailySummaries = () => useReportStore.getState().updatedDailySummaries;
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  /**
   * Reads the pristine, server-loaded daily heart-rate summaries held in the report store. Pulls originalDailySummaries straight off the store snapshot rather than subscribing, so callers inside emitter handlers see the current value.
   *
   * @returns The original daily summaries as last loaded from the server.
   */
  const getOriginalDailySummaries = () => useReportStore.getState().originalDailySummaries;
  const setMinHrTabActive = useReportStore((s) => s.actions.setMinHrTabActive);
  const minHrTabActive = useReportStore((s) => s.minHrTabActive);

  const activeItemRef = useRef(null);

  // Sync hrData with hrDataDefault
  useEffect(() => {
    if (hrDataDefault) {
      setHrData(_.cloneDeep(hrDataDefault));
    }
  }, [hrDataDefault]);

  useEffect(() => {
    if (minHrsInDateRecoil) {
      setMinHrsInDate(_.cloneDeep(minHrsInDateRecoil));
    }
    if (maxHrsInDateRecoil) {
      setMaxHrsInDate(_.cloneDeep(maxHrsInDateRecoil));
    }
  }, [minHrsInDateRecoil, maxHrsInDateRecoil]);

  // Check and update daily summaries
  useEmitter('CHECK_UPDATED_DAILY_SUMMARY', () => {
    const selectedDate = getSelectedDateValue();
    const originalHRSDay = getOriginalDailySummaries().find((x) => x.date === selectedDate);
    const updatedHRSDay = getUpdatedDailySummaries().find((x) => x.date === selectedDate);

    if (
      originalHRSDay &&
      updatedHRSDay &&
      _.isEqual(
        _.pick(originalHRSDay, ['date', 'minHr', 'maxHr', 'avgHr']),
        _.pick(updatedHRSDay, ['date', 'minHr', 'maxHr', 'avgHr']),
      )
    ) {
      setUpdatedDailySummaries(getUpdatedDailySummaries().filter((x) => x.date !== selectedDate));
    }
  });

  // Scroll to active item and handle tab changes
  useEffect(() => {
    if (activeItemRef.current)
      activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (tabHrActive === HR_TYPE.MAX_HR && hrData) {
      if (hrData.maxHr?.time) {
        if (!jumpDate) {
          setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MAX_HR);
          setSelectedHrTime(hrData.maxHr.time);
          setJumpDate(hrData.maxHr.time);
          setJumpPortion(0);
        }
      }
    } else if (tabHrActive === HR_TYPE.MIN_HR && hrData) {
      if (hrData.minHr?.time) {
        if (!jumpDate) {
          setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MIN_HR);
          setSelectedHrTime(hrData.minHr.time);
          setJumpDate(hrData.minHr.time);
          setJumpPortion(0);
        }
      }
    }
  }, [tabHrActive, hrData]);

  useEffect(() => {
    if (minHrTabActive) {
      setTabHrActive(HR_TYPE.MIN_HR);
    } else {
      setTabHrActive(HR_TYPE.MAX_HR);
    }
  }, [minHrTabActive]);

  /**
   * Reconciles an edited heart-rate summary for the selected day against the server defaults and records it as a pending change. Compares each of minHr, maxHr and avgHr with hrDataDefault, converting changed times to the report timezone and attaching the matching beat window from the min or max thumbnail list while unchanged fields keep the default, rebuilds the suggested beat lists, then upserts the day into the pending daily summaries and fires CHECK_UPDATED_DAILY_SUMMARY; failures are logged to the console.
   *
   * @param updatedHrData - The day's heart-rate summary carrying the reviewer's edits.
   * @returns Nothing.
   */
  const onUpdatedHrData = (updatedHrData: IHrSummary) => {
    try {
      const selectedDateValue = getSelectedDateValue();
      // IHrData (shared/model/heartRate.ts) doesn't declare `hrsOnBeat`, though this
      // code reads/writes it at runtime — pre-existing gap in that model, cast as any here.
      const updatedFields: any = ['minHr', 'maxHr', 'avgHr'].reduce((acc: any, key) => {
        const defaultHr: any = (hrDataDefault as any)[key];
        const updatedHr: any = (updatedHrData as any)[key];
        const isChanged =
          key === 'avgHr'
            ? Number(updatedHr.value) !== defaultHr.value
            : Number(updatedHr.value) !== defaultHr.value || updatedHr.time !== defaultHr.time;

        if (isChanged) {
          const foundItem: any =
            key === 'minHr'
              ? _.find(minHrsInDate, (x) => x.start === updatedHr.time)
              : _.find(maxHrsInDate, (x) => x.start === updatedHr.time);

          acc[key] = {
            time: convertToMomentJs(updatedHr.time, timezoneOffset).toISOString(),
            value: Number(updatedHr.value),
            hrsOnBeat: foundItem
              ? { head: foundItem.start, tail: foundItem.stop, avgHr: defaultHr.hrsOnBeat.avgHr }
              : defaultHr.hrsOnBeat,
            userUpdated: foundItem?.userUpdated,
          };
        } else {
          acc[key] = defaultHr;
        }
        return acc;
      }, {});

      updatedFields.suggestHrsOnBeat = {
        maxHr: maxHrsInDate.map((item: any) => {
          if (item.start === updatedFields.maxHr.time) {
            return {
              head: item.start,
              tail: item.stop,
              avgHr: updatedFields.maxHr.value,
              userUpdated: updatedFields.maxHr.userUpdated,
            };
          }
          return {
            head: item.start,
            tail: item.stop,
            avgHr: item.value,
            userUpdated: item?.userUpdated,
          };
        }),
        minHr: minHrsInDate.map((item: any) => {
          if (item.start === updatedFields.minHr.time) {
            return {
              head: item.start,
              tail: item.stop,
              avgHr: updatedFields.minHr.value,
              userUpdated: updatedFields.minHr.userUpdated,
            };
          }
          return {
            head: item.start,
            tail: item.stop,
            avgHr: item.value,
            userUpdated: item?.userUpdated,
          };
        }),
      };

      const updatedDailySummary: IDailySummary = {
        date: selectedDateValue,
        ...updatedFields,
      };

      const cloneUpdatedDailySummaries = _.cloneDeep(getUpdatedDailySummaries());
      const foundDailySummary: any = cloneUpdatedDailySummaries.find(
        (x) => x.date === selectedDateValue,
      );

      if (foundDailySummary) {
        _.assign(foundDailySummary, updatedDailySummary);
      } else {
        cloneUpdatedDailySummaries.push(updatedDailySummary);
      }
      setUpdatedDailySummaries(cloneUpdatedDailySummaries);
      emitter.emit('CHECK_UPDATED_DAILY_SUMMARY');
    } catch (error) {
      console.error('Error in onUpdatedHrData:', error);
    }
  };

  // This function is used to update the min and max HR data
  /**
   * Applies a reviewer-entered heart-rate value and time to the min, max or average field of the selected day. Dispatches on the HR type constant to pick the field, then builds the amended summary, pushes it into the pending daily-summary edits and mirrors it into local state.
   *
   * @param type - Which heart-rate figure is being edited, matching one of the HR_TYPE constants.
   * @param value - The corrected beats-per-minute figure.
   * @param time - Timestamp of the beat the value was taken from, when the edit came from a thumbnail.
   * @returns Nothing.
   */
  const handleUpdateValueMinMaxHr = (type: string, value: number, time?: string) => {
    /**
     * Rewrites one field of the day's heart-rate summary with the value and time captured by the surrounding edit. Spreads the current summary, overwrites the named field, forwards the result to the pending-edit reconciler and stores it locally so the thumbnails re-render.
     *
     * @param key - The heart-rate field to overwrite: minHr, maxHr or avgHr.
     * @returns Nothing.
     */
    const updateHrData = (key: 'minHr' | 'maxHr' | 'avgHr') => {
      const newHrData = {
        ...hrData,
        [key]: {
          ...hrData[key],
          value,
          time,
        },
      };
      onUpdatedHrData(newHrData);
      setHrData(newHrData);
    };

    if (type === HR_TYPE.MIN_HR) {
      updateHrData('minHr');
    } else if (type === HR_TYPE.MAX_HR) {
      updateHrData('maxHr');
    } else if (type === HR_TYPE.AVG_HR) {
      updateHrData('avgHr');
    }
  };

  // This function is used to toggle the channel buttons
  /**
   * Shows or hides one ECG channel in the thumbnail strip. Adds the channel when absent and removes it when present, but keeps the previous list whenever the removal would leave no channel visible.
   *
   * @param channel - The one-based ECG channel number being toggled.
   * @returns Nothing.
   */
  const toggleChannel = (channel: number) => {
    setChannelsThumbnail((prev: number[]) => {
      const updatedChannels = prev.includes(channel)
        ? prev.filter((ch) => ch !== channel)
        : [...prev, channel];
      // Prevent changes if only one channel remains
      return updatedChannels.length === 0 ? prev : updatedChannels;
    });
  };

  useEffect(() => {
    setIsLoading(!isRenderViewer);
  }, [isRenderViewer]);

  return {
    tabHrActive,
    setTabHrActive,
    setMinHrTabActive,
    channelsThumbnail,
    toggleChannel,
    minHrsInDate,
    maxHrsInDate,
    hrData,
    isLoading,
    highPassThumbnail,
    gainThumbnail,
    activeItemRef,
    handleUpdateValueMinMaxHr,
  };
};

export default useDailySummaryHrsThumbnail;
